import React, { useEffect, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowRight, BookOpen, Bot, Check, ChevronDown, ChevronUp, CircleAlert, CircleCheck, Coins, Copy, ListChecks,
  LoaderCircle, Lock, Maximize2, X,
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/lib/auth";
import { hasMinRole, type SystemRole } from "@/lib/rbac";
import { cn } from "@/lib/utils";
import { copyText } from "@/lib/mcp";
import { capture } from "@/lib/posthog";
import { grantedCredits, useSetupJourney, useSaveSetupJourney } from "@/hooks/use-setup-journey";
import { useSetupProgress, type SetupStepId } from "@/hooks/use-setup-progress";
import { useOrgDomain } from "@/hooks/use-org-domain";
import { useOrchestratorMessages, useOrchestratorSessions } from "@/hooks/use-orchestrator";
import { firstIntegrationTestIds as tid } from "@/testids/firstIntegrationTestIds";
import { AiSetupPopover } from "@/pages/onboarding/AiTools";
import { reportStepFailed, trackOnboardingClicks } from "@/pages/onboarding/analytics";
import { defaultAiTool, type AiToolId } from "@/pages/onboarding/ai-tools";
import { blockedTodo, connectorIdFromTodos, pendingAsk } from "@/pages/onboarding/agent-build";
import { DOCS_LINKS } from "@/pages/onboarding/docs-links";
import { embedSnippet } from "@/pages/onboarding/embed-snippet";
import { nameFromDomain, useProductResearch } from "@/pages/onboarding/product-research";
import { useProductRegistration } from "@/pages/onboarding/product-registration";
import { SETUP_CREDITS, STEP_CREDITS, STEP_TITLES } from "@/pages/onboarding/ob3";
import { OPEN_SETUP_PANEL, STEP_START, journeyStartOf, onboardingPath, resumeScreen, screenFromPath } from "@/pages/onboarding/screens";
import "@/pages/onboarding/onboarding.css";

const STEP_IDS: SetupStepId[] = ["product", "integrations", "embed", "customers"];
const RESEARCH_PATH = onboardingPath("research");
const BUILDING_PATH = onboardingPath("building");

/** Where each step lives in the app, its docs, and its line when it is not done. */
const STEP_INFO: Record<SetupStepId, { line: string; inApp: string; to: string; docs: string }> = {
  product: { line: "Connect your product’s API or webhooks.", inApp: "Integrations › Your API", to: "/your-product", docs: DOCS_LINKS.connectProduct },
  integrations: { line: "Pick one, or select all to offer the whole catalog.", inApp: "Integrations › Connectors", to: "/integrations?tab=connectors", docs: DOCS_LINKS.chooseIntegrations },
  embed: { line: "Paste one snippet into your product. One time.", inApp: "Widgets › Embed", to: "/widgets", docs: DOCS_LINKS.embedWidget },
  customers: { line: "Register your customers from your backend; they connect in your widget.", inApp: "Customers", to: "/customers", docs: DOCS_LINKS.customersStep },
};

/**
 * Where the first integration stands, for the pieces of it the platform shows
 * outside /onboarding: the rail's pill, the status pill over every page, Home's
 * banner and the build card. Mount only for builders (see useSetupProgress).
 */
export function useFirstIntegration() {
  const navigate = useNavigate();
  const journeyQuery = useSetupJourney();
  const save = useSaveSetupJourney();
  const progress = useSetupProgress();
  const orgDomain = useOrgDomain();
  const journey = journeyQuery.data;
  const product = journey?.product ?? null;
  const domain = product?.domain ?? orgDomain.data ?? null;
  const productName = product?.name ?? (domain ? nameFromDomain(domain) : "your product");
  const productDone = progress.steps.find((s) => s.id === "product")?.state === "done";

  const onResearch = journey?.screen === RESEARCH_PATH && !productDone;
  const research = useProductResearch(onResearch ? domain : null, { start: false });

  const threadId = journey?.productThreadId ?? null;
  const onBuild = journey?.screen === BUILDING_PATH && !!threadId;
  // Watched until step 1 is done, wherever the journey is: a build that ends
  // while the person is on another page still has to be registered.
  const watchBuild = !!threadId && (onBuild || !productDone);
  const sessions = useOrchestratorSessions({ enabled: watchBuild, poll: 10_000 });
  const messages = useOrchestratorMessages(onBuild ? threadId ?? undefined : undefined, { poll: 10_000 });
  const todos = sessions.data?.find((s) => s.threadId === threadId)?.todos ?? [];
  const buildDone = todos.length > 0 && todos.every((t) => t.status === "completed");
  // Ticked todos only say the run ended; step 1 is done once the product is registered.
  const { outcome: build } = useProductRegistration({
    buildDone: watchBuild && buildDone,
    productDone,
    connectorId: connectorIdFromTodos(todos),
  });
  const ask = pendingAsk(messages.data ?? []);
  // A connection the agent is waiting on needs the person as much as a question does.
  const questions = ask.questions.length + (ask.connect ? 1 : 0);
  // The agent stopped on a todo it marked blocked: the build needs the person, it is not running.
  const blocked = onBuild && !!blockedTodo(todos, { running: false, asking: questions > 0 || ask.handOff });

  const done = progress.steps.filter((s) => s.state === "done").length;
  const earned = grantedCredits(journey);

  const state =
    journey?.status === "completed" || progress.complete ? "complete"
      : blocked ? "build-failed"
      : onBuild && (build.kind === "running" || build.kind === "registering") ? "building"
        : onBuild && build.kind === "failed" ? "build-failed"
          : onBuild && productDone ? "built"
          : onResearch && research.running ? "researching"
            : onResearch ? "research-ready"
              : journey?.status === "skipped" && !productDone ? "not-finished"
                : "open";

  /** Back into the guided screens: where they left off, or a step's start. */
  const resume = (path?: string) => {
    const firstOpen = progress.steps.findIndex((s) => s.state !== "done");
    // Step 1 with a build behind it resumes on the build, which says how it ended.
    const startOf = journeyStartOf(journey);
    const screen = resumeScreen(screenFromPath(journey?.screen), firstOpen, startOf) ?? startOf(((firstOpen + 1) || 1) as 1 | 2 | 3 | 4);
    const to = path ?? onboardingPath(screen);
    void save.mutateAsync({ status: "in_progress", screen: to }).catch((err) => reportStepFailed("setup_panel", 0, "journey_save_failed", err)).finally(() => navigate(to));
  };

  /** Back to step 2's build, on its own screen. */
  const integrationThreadId = journey?.integrationThreadId ?? null;
  const openIntegrationThread = () => {
    if (integrationThreadId) resume(onboardingPath("integrationBuilding"));
  };


  return { journey, progress, product, productName, domain, productDone, state, done, earned, todos, questions, resume, save, integrationThreadId, openIntegrationThread };
}

/** Mounted only for the roles that can set up, since its reads run on mount. */
export function canSetUpFirstIntegration(role: string | undefined, endOrgId: string | undefined): boolean {
  return hasMinRole((role ?? "end_user") as SystemRole, "developer") && !endOrgId;
}

function useCanSetUp(): boolean {
  const { userContext } = useAuth();
  return canSetUpFirstIntegration(userContext?.role, userContext?.endOrgId);
}

/* ── The panel: four steps, what each needs, and the AI tool ─────────────── */

function StepLinks({ id }: { id: SetupStepId }): React.ReactElement {
  const info = STEP_INFO[id];
  return (
    <div className="t12 flex! flex-wrap! items-center! [gap:6px_14px]! [margin-top:6px]!">
      <a className="lnk inline-flex! items-center! gap-[4px]!" href={info.docs} target="_blank" rel="noreferrer"><BookOpen className="i xs" aria-hidden="true" /> Docs</a>
      <RouterLink className="sub inline-flex! items-center! gap-[4px]!" to={info.to}>In the app: {info.inApp}</RouterLink>
    </div>
  );
}

export function FirstIntegrationPanel({ onClose }: { onClose: () => void }): React.ReactElement {
  const fi = useFirstIntegration();
  const firstOpen = fi.progress.steps.findIndex((s) => s.state !== "done");
  const [open, setOpen] = useState<number>(firstOpen === -1 ? 0 : firstOpen);
  const [copied, setCopied] = useState(false);
  const [popOpen, setPopOpen] = useState(false);
  const tool = (fi.journey?.aiTool as AiToolId | null) ?? defaultAiTool(undefined).tool;

  const lineFor = (id: SetupStepId, done: boolean): string => {
    if (id === "product") {
      if (done) return `${fi.productName} is connected.`;
      if (fi.state === "building") return "Building · the fastn agent is connecting it.";
      if (fi.state === "build-failed") return "The build ended without connecting it. Open it to finish.";
      if (fi.state === "researching") return `Learning about ${fi.productName}…`;
      if (fi.product?.apiDocsUrl) return "API docs found. Review to finish.";
      return STEP_INFO.product.line;
    }
    if (id === "integrations" && done && fi.journey?.useCase) return fi.journey.useCase;
    if (id === "embed" && !done) return "Paste it where customers manage settings.";
    return STEP_INFO[id].line;
  };

  return (
    <div className="ob3" onClickCapture={trackOnboardingClicks("setup_panel")}>
      <div className="ds [background:transparent]!">
        <div className="fpop ob3-fpop-static" data-testid={tid.panel}>
          <div className="flex! items-center! gap-[8px]!">
            <span className="b flex-1! ob3-fs15">Your first integration</span>
            <span className="mono t12 sub" data-testid={tid.panelCount}>{fi.done} of 4 done</span>
            <button className="btn btn--ghost btn--icon btn--sm [margin-right:-6px]! cursor-pointer!" type="button" onClick={onClose} aria-label="Close" data-testid={tid.panelClose}>
              <X className="i sm" aria-hidden="true" />
            </button>
          </div>
          <div className="t12 mut">Four steps to your first customer.</div>
          <div className="t12 sub [margin-bottom:6px]! flex! items-center! gap-[5px]! [margin-top:2px]!">
            <Coins className="i xs" aria-hidden="true" /> {fi.earned} of {SETUP_CREDITS} AI credits earned
          </div>
          {STEP_IDS.map((id, i) => {
            const step = fi.progress.steps.find((s) => s.id === id);
            const done = step?.state === "done";
            const isNext = i === firstOpen;
            const expanded = open === i;
            const blocked = id === "customers" && !fi.productDone;
            return (
              <div key={id} className="gst flex-col! [gap:0]!" data-testid={tid.step(id)} data-state={done ? "done" : isNext ? "next" : "waiting"}>
                <button className="flex! gap-[12px]! items-center! w-full! text-left! cursor-pointer!" type="button" onClick={() => setOpen(expanded ? -1 : i)} aria-expanded={expanded}>
                  <span className={cn("num", done && "done", isNext && "on")}>{done ? <Check className="i xs" aria-hidden="true" /> : i + 1}</span>
                  <span className="b t13 flex-1! flex! items-center! gap-[8px]! flex-wrap!">
                    {STEP_TITLES[i]}
                    {id === "product" && !done && fi.journey?.status === "skipped" && <span className="badge badge--warn">Required to go live</span>}
                    {isNext && id !== "product" && <span className="tag">Next</span>}
                    {blocked && !done && <span className="t12 sub [font-weight:400]! inline-flex! items-center! gap-[4px]!"><Lock className="i xs" aria-hidden="true" /> Needs step 1</span>}
                  </span>
                  {expanded ? <ChevronUp className="i xs" aria-hidden="true" /> : <ChevronDown className="i xs" aria-hidden="true" />}
                </button>
                {expanded && (
                  <div className="[padding-left:38px]! [margin-top:2px]!">
                    <div className="t12 mut [line-height:1.5]!">{lineFor(id, done)}</div>
                    {id === "embed" && !done && (
                      <div className="box [margin-top:10px]! [padding:12px]! [background:var(--bgColor-default)]! flex! flex-col! gap-[10px]!">
                        <div className="flex! gap-[6px]! items-center!">
                          <div className="code flex-1! min-w-0! [padding:7px_10px]! ob3-fs11 whitespace-nowrap! overflow-hidden! [text-overflow:ellipsis]!">{embedSnippet()}</div>
                          <button type="button" className="btn btn--secondary btn--sm cursor-pointer!" onClick={async () => setCopied(await copyText(embedSnippet()))} data-testid={tid.copySnippet}>
                            {copied ? <Check className="i xs" aria-hidden="true" /> : <Copy className="i xs" aria-hidden="true" />} {copied ? "Copied" : "Copy"}
                          </button>
                        </div>
                        <div className="t12 mut flex! items-center! gap-[6px]!"><span className="spin flex!"><LoaderCircle className="i xs" aria-hidden="true" /></span>Waiting for it to load in your product…</div>
                      </div>
                    )}
                    {id === "customers" && !done && (
                      <div className="t12 mut flex! items-center! gap-[6px]! [margin-top:8px]!" data-testid={tid.customersWaiting}>
                        <span className="spin flex!"><LoaderCircle className="i xs" aria-hidden="true" /></span>Waiting for your first customer to connect in your widget…
                      </div>
                    )}
                    {!done && isNext && id !== "embed" && (
                      <button
                        type="button"
                        className="btn btn--primary btn--sm [margin-top:10px]! cursor-pointer!"
                        onClick={() => {
                          capture("home_step_clicked", { step: id, state: "next" });
                          onClose();
                          if (id === "integrations" && fi.integrationThreadId) fi.openIntegrationThread();
                          else fi.resume(id === "product" ? undefined : onboardingPath(STEP_START[(i + 1) as 1 | 2 | 3 | 4]));
                        }}
                        data-testid={tid.continueSetup(id)}
                      >
                        Continue setup <ArrowRight className="i xs" aria-hidden="true" />
                      </button>
                    )}
                    <StepLinks id={id} />
                  </div>
                )}
              </div>
            );
          })}
          <div className="[padding:10px_0_4px]! [border-top:1px_solid_var(--borderColor-muted)]!">
            <AiSetupPopover
              mode="continue"
              tool={tool}
              fromAnswer={false}
              onTool={(t) => void fi.save.mutateAsync({ aiTool: t })}
              open={popOpen}
              onOpenChange={setPopOpen}
              trigger={<><Bot className="i xs" aria-hidden="true" /> Do the steps in your AI tool</>}
              triggerClassName="lnk t12 inline-flex! items-center! gap-[6px]! cursor-pointer!"
              testId={tid.aiTools}
              onDone={() => { setPopOpen(false); onClose(); void fi.save.mutateAsync({ mode: "ai_tool" }); }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── The rail's pill ─────────────────────────────────────────────────────── */

export function FirstIntegrationRailPill(): React.ReactElement | null {
  if (!useCanSetUp()) return null;
  return <RailPill />;
}

function RailPill(): React.ReactElement | null {
  const fi = useFirstIntegration();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  // Opened by a setup prompt being sent: it stays up beside the agent chat,
  // which the person keeps typing in, until they close it.
  const [pinned, setPinned] = useState(false);
  useEffect(() => {
    if ((location.state as Record<string, unknown> | null)?.[OPEN_SETUP_PANEL]) {
      setOpen(true);
      setPinned(true);
    }
  }, [location.key, location.state]);
  const close = () => { setOpen(false); setPinned(false); };
  if (fi.state === "complete") return null;
  const live = fi.state === "not-finished" || fi.state === "building" || fi.state === "build-failed";

  return (
    <div className="ob3">
      <div className="ds [background:transparent]!">
        <DropdownMenu open={open} onOpenChange={(o) => (o ? setOpen(true) : close())} modal={!pinned}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={cn("fibtn cursor-pointer! text-left!", live && "fibtn--live", open && "open")}
              aria-label={`Your first integration, ${fi.done} of 4 done`}
              data-testid={tid.railPill}
            >
              <ListChecks className="i sm" aria-hidden="true" />
              <span className="flex-1! min-w-0! whitespace-nowrap! overflow-hidden! [text-overflow:ellipsis]!">
                {fi.state === "researching" ? "Researching…" : live ? (
                  <span className="flex! flex-col! [line-height:1.25]!">
                    <span>Your first integration</span>
                    <span className="t12 [color:var(--fgColor-attention)]! flex! items-center! gap-[5px]!">
                      <span className="livedot" />{fi.state === "building" ? "Step 1 building…" : fi.state === "build-failed" ? "Step 1 needs you" : "Not finished"}
                    </span>
                  </span>
                ) : "Your first integration"}
              </span>
              {fi.state === "researching" && <span className="spin flex! [color:var(--fgColor-subtle)]!"><LoaderCircle className="i xs" aria-hidden="true" /></span>}
              {fi.state === "research-ready" && <span className="w-[8px]! h-[8px]! [border-radius:50%]! [background:var(--fgColor-success)]! [flex:0_0_8px]!" />}
              <span className="t12 sub [font-variant-numeric:tabular-nums]!" data-testid={tid.railPillCount}>{fi.done}/4</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side="right"
            align="end"
            sideOffset={12}
            className="overflow-visible! border-0! bg-transparent! p-0! shadow-none!"
            onInteractOutside={(e) => { if (pinned) e.preventDefault(); }}
            onFocusOutside={(e) => { if (pinned) e.preventDefault(); }}
            onCloseAutoFocus={(e) => { if (pinned) e.preventDefault(); }}
            onOpenAutoFocus={(e) => { if (pinned) e.preventDefault(); }}
          >
            <FirstIntegrationPanel onClose={close} />
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

/* ── Over every page: research status, Home's banner, the build card ───── */

export function FirstIntegrationStatus(): React.ReactElement | null {
  if (!useCanSetUp()) return null;
  return <Status />;
}

/**
 * For the onboarding screens, which render outside the platform shell that
 * mounts Status: watching the build is what registers the product when it
 * ends, wherever the person is.
 */
export function RegisterBuiltProduct(): null {
  useFirstIntegration();
  return null;
}

function Status(): React.ReactElement | null {
  const fi = useFirstIntegration();
  const location = useLocation();
  const [dismissed, setDismissed] = useState<string | null>(null);
  const onHome = location.pathname === "/";
  const name = fi.productName;
  const finished = fi.todos.filter((t) => t.status === "completed").length;
  const pct = fi.todos.length ? Math.round((finished / fi.todos.length) * 100) : 4;

  const pill = (() => {
    if (fi.state === "researching") {
      return (
        <div className="[flex:0_0_auto]! [margin:16px_auto_0]! [padding:6px_8px_6px_14px]! [border-radius:var(--borderRadius-full)]! [border:1px_solid_var(--borderColor-default)]! [background:var(--bgColor-muted)]! flex! items-center! gap-[10px]! [font-size:13px]! w-fit!" data-testid={tid.researchPill}>
          <span className="spin flex!"><LoaderCircle className="i xs" aria-hidden="true" /></span>
          <span>Learning about {name}…</span>
          <button type="button" className="btn btn--ghost btn--sm cursor-pointer!" onClick={() => fi.resume(RESEARCH_PATH)} data-testid={tid.researchView}>View</button>
        </div>
      );
    }
    if (fi.state === "research-ready") {
      return (
        <div className="[flex:0_0_auto]! [margin:16px_auto_0]! [padding:6px_8px_6px_14px]! [border-radius:var(--borderRadius-full)]! [border:1px_solid_var(--borderColor-default)]! [background:var(--bgColor-muted)]! flex! items-center! gap-[10px]! [font-size:13px]! w-fit!" data-testid={tid.researchReadyPill}>
          <span className="[color:var(--fgColor-success)]! flex!"><CircleCheck className="i xs" aria-hidden="true" /></span>
          <span>Research on {name} is ready</span>
          <button type="button" className="btn btn--primary btn--sm cursor-pointer!" onClick={() => fi.resume(RESEARCH_PATH)} data-testid={tid.continueSetup("research")}>
            Continue setup <ArrowRight className="i xs" aria-hidden="true" />
          </button>
        </div>
      );
    }
    if (fi.state === "not-finished" && onHome) {
      return (
        <div className="ob3-warn-banner [flex:0_0_auto]! flex! items-center! gap-[12px]! [margin:16px_40px_0]! [padding:10px_12px_10px_16px]! [border-radius:var(--borderRadius-large)]!" data-testid={tid.notFinishedBanner}>
          <span className="livedot" />
          <span className="t13 flex-1!"><b>Your setup isn’t finished.</b> <span className="mut">You can’t go live, and your {SETUP_CREDITS} setup AI credits stay locked until you connect your product.</span></span>
          <button type="button" className="btn btn--primary btn--sm cursor-pointer!" onClick={() => fi.resume()} data-testid={tid.continueSetup("banner")}>
            Continue setup <ArrowRight className="i xs" aria-hidden="true" />
          </button>
        </div>
      );
    }
    return null;
  })();

  const card = fi.state === "building" ? (
    <div className="box ob3-float fixed! right-[24px]! bottom-[24px]! [z-index:30]! w-[340px]! [padding:14px_16px]! flex! flex-col! gap-[10px]! [background:var(--bgColor-default)]!" data-testid={tid.buildCard}>
      <div className="flex! items-center! gap-[10px]!">
        <span className="spin flex!"><LoaderCircle className="i sm" aria-hidden="true" /></span>
        <span className="b t13 flex-1!">Connecting {name} to fastn</span>
        <button type="button" className="btn btn--ghost btn--sm cursor-pointer!" title="Back to the build" onClick={() => fi.resume(BUILDING_PATH)} data-testid={tid.buildCardOpen}>
          <Maximize2 className="i xs" aria-hidden="true" /> Open
        </button>
      </div>
      <div className="h-[4px]! [border-radius:99px]! [background:var(--a-9)]!">
        <div className="h-full! [border-radius:99px]! [background:var(--fgColor-default)]!" style={{ width: `${pct}%` }} />
      </div>
      {fi.questions > 0 && (
        <button type="button" className="t12 flex! items-center! gap-[6px]! [color:var(--fgColor-attention)]! cursor-pointer! text-left!" onClick={() => fi.resume(BUILDING_PATH)} data-testid={tid.buildCardQuestion}>
          <span className="livedot" />The agent has {fi.questions} {fi.questions === 1 ? "question" : "questions"}. Answer it
        </button>
      )}
      <div className="t12 sub flex! items-center! gap-[8px]!"><span className="flex-1!">{finished} of {fi.todos.length || 4} steps done</span></div>
    </div>
  ) : fi.state === "build-failed" && dismissed !== "build-failed" ? (
    <div className="box ob3-float fixed! right-[24px]! bottom-[24px]! [z-index:30]! w-[340px]! [padding:14px_16px]! flex! flex-col! gap-[12px]! [background:var(--bgColor-default)]!" data-testid={tid.buildFailedCard}>
      <div className="flex! items-start! gap-[10px]!">
        <span className="flex! [color:var(--fgColor-attention)]! [margin-top:1px]!"><CircleAlert className="i sm" aria-hidden="true" /></span>
        <span className="flex-1!"><span className="b t13 block!">{name} is not connected yet</span><span className="t12 mut">The build ended without connecting it.</span></span>
      </div>
      <div className="flex! gap-[8px]! justify-end!">
        <button type="button" className="btn btn--ghost btn--sm cursor-pointer!" onClick={() => setDismissed("build-failed")} data-testid={tid.builtLater}>Later</button>
        <button type="button" className="btn btn--primary btn--sm cursor-pointer!" onClick={() => fi.resume(BUILDING_PATH)} data-testid={tid.buildFailedOpen}>
          Open <ArrowRight className="i xs" aria-hidden="true" />
        </button>
      </div>
    </div>
  ) : fi.state === "built" && dismissed !== "built" ? (
    <div className="box ob3-float fixed! right-[24px]! bottom-[24px]! [z-index:30]! w-[340px]! [padding:14px_16px]! flex! flex-col! gap-[12px]! [background:var(--bgColor-default)]!" data-testid={tid.builtCard}>
      <div className="flex! items-start! gap-[10px]!">
        <span className="flex! [color:var(--fgColor-success)]! [margin-top:1px]!"><CircleCheck className="i sm" aria-hidden="true" /></span>
        <span className="flex-1!"><span className="b t13 block!">{name} is connected to fastn</span><span className="t12 mut">Step 1 done</span></span>
      </div>
      <div className="flex! gap-[8px]! justify-end!">
        <button type="button" className="btn btn--ghost btn--sm cursor-pointer!" onClick={() => setDismissed("built")} data-testid={tid.builtLater}>Later</button>
        <button type="button" className="btn btn--primary btn--sm cursor-pointer!" onClick={() => fi.resume(onboardingPath("integrations"))} data-testid={tid.builtContinue}>
          Continue · +{STEP_CREDITS[0]} AI credits <ArrowRight className="i xs" aria-hidden="true" />
        </button>
      </div>
    </div>
  ) : null;

  if (!pill && !card) return null;
  return (
    <div className="ob3">
      <div className="ds [background:transparent]! flex! flex-col!">
        {pill}
        {card}
      </div>
    </div>
  );
}


/* ── Settings > General: the product the integrations are built around ───── */

export function YourProductCard(): React.ReactElement | null {
  const canSetUp = useCanSetUp();
  if (!canSetUp) return null;
  return <YourProductCardBody />;
}

function YourProductCardBody(): React.ReactElement | null {
  const fi = useFirstIntegration();
  if (!fi.domain && !fi.product) return null;
  const line = fi.productDone
    ? `${fi.productName} is connected to fastn.`
    : fi.product?.apiDocsUrl
      ? "API docs found, not confirmed. Needed to go live."
      : "Not connected yet. Needed to go live.";
  return (
    <div className="stCard" data-testid={tid.productCard}>
      <div className="stCardHead">
        <div className="stCardT">Your product</div>
        <span className={cn("fx-badge ml-auto", fi.productDone ? "fx-badge--ok" : "fx-badge--warn")}>
          {fi.productDone ? "Connected" : "Not connected"}
        </span>
      </div>
      <div className="stCardBody">
        <div className="ob3 flex items-center gap-3.5">
          <span className="hm w-9! h-9! text-base!" aria-hidden="true">{(fi.productName.trim()[0] ?? "?").toUpperCase()}</span>
          <span className="flex-1 min-w-0">
            <span className="block font-semibold text-[13px]">{fi.domain ?? fi.productName}</span>
            <span className="text-xs text-[var(--fgColor-muted)]">{line}</span>
          </span>
          {!fi.productDone && (
            <button type="button" className="fx-btn fx-btn--primary" onClick={() => fi.resume()} data-testid={tid.productCardContinue}>
              Continue setup
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
